/**
 * Daily-rollup views fronting the `DailyRollup{Grain}_Agent` sources and
 * `DailyRollupOutput_Well`.
 *
 * Five grains (Asset/Company/Location/Reservoir/Well) stay WIDE: each source
 * carries NO column prefix, so the shared `DAILY_ROLLUP` shape is authored with
 * `suffix === canonical` and applied with the default empty prefix — a pure
 * `(columns …)` projection, no `(rename …)`. The FIELD grain is concern-SPLIT
 * into three sub-views — `AllocatedOutputFieldDaily`, `PotentialOutputFieldDaily`,
 * `TargetOutputFieldDaily` — each de-prefixing one measure family to clean phase
 * canonicals (`oil`/`gas`/`water`/`gasInjection`/`waterInjection`, plus `*Rate`
 * for the allocated actuals). The agent only ever queries the field rollup, and
 * does so per concern; the other grains carry the identical 20-measure set and
 * stay wide.
 *
 * All are `createReshapedView` (rewrite) views — pure de-prefix/projection over
 * the source schema, owning no fetch — so they need no `deps`; they are a
 * module-level constant spread into `createAppVirtualViews`, mirroring
 * `networkInputsViews`.
 */

import type { DataModelView } from '#core/lib/cognite/view.ts';

import { createReshapedView, type VirtualView } from '../createReshapedView.ts';
import {
  ALLOCATED_OUTPUT,
  ASSET_RELATION,
  COMPANY_RELATION,
  DAILY_ROLLUP,
  FIELD_RELATION,
  IDENTITY,
  LOCATION_RELATION,
  POTENTIAL_OUTPUT,
  RESERVOIR_RELATION,
  type Shape,
  TARGET_OUTPUT_DAILY,
  WELL_RELATION,
} from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import {
  DailyRollupAssetAgentView,
  DailyRollupCompanyAgentView,
  DailyRollupFieldAgentView,
  DailyRollupLocationAgentView,
  DailyRollupOutputWellView,
  DailyRollupReservoirAgentView,
} from '#schema/generated/ipso/views.ts';

/** One wide daily-rollup grain surface: its registry name, source view, and the grain ref it keeps. */
type DailyRollupSpec = Omit<ViewFamilySpec, 'description'> & {
  /** The grain (used in the agent-facing description). */
  readonly grain: string;
  /** The grain ref's canonical column name (used in the agent-facing description). */
  readonly grainRef: string;
  /** The source CDF view this reshapes. */
  readonly source: DataModelView;
  /** The grain direct-relation kept unrenamed alongside identity. */
  readonly relation: Shape;
};

/**
 * The five WIDE daily-rollup grain surfaces, declared as data. Each de-prefixes
 * the shared 20-measure `DAILY_ROLLUP` family over one grain, keeping identity +
 * the grain's direct-relation. `DailyRollupWell` fronts `DailyRollupOutput_Well`,
 * whose `entityName` is the well grain relation (despite the name). The FIELD
 * grain is concern-split separately — see `DAILY_ROLLUP_FIELD_SUBVIEWS`.
 */
const DAILY_ROLLUP_SPECS: readonly DailyRollupSpec[] = [
  {
    name: 'DailyRollupAsset',
    grain: 'ASSET',
    grainRef: 'assetEntity',
    source: DailyRollupAssetAgentView,
    relation: ASSET_RELATION,
  },
  {
    name: 'DailyRollupCompany',
    grain: 'COMPANY',
    grainRef: 'companyEntity',
    source: DailyRollupCompanyAgentView,
    relation: COMPANY_RELATION,
  },
  {
    name: 'DailyRollupLocation',
    grain: 'LOCATION',
    grainRef: 'locationEntity',
    source: DailyRollupLocationAgentView,
    relation: LOCATION_RELATION,
  },
  {
    name: 'DailyRollupReservoir',
    grain: 'RESERVOIR',
    grainRef: 'reservoirEntity',
    source: DailyRollupReservoirAgentView,
    relation: RESERVOIR_RELATION,
  },
  {
    name: 'DailyRollupWell',
    grain: 'WELL',
    grainRef: 'entityName',
    source: DailyRollupOutputWellView,
    relation: WELL_RELATION,
  },
];

/** One concern-split FIELD-grain sub-view: registry name, its clean measure shape, and the concern blurb. */
type DailyRollupFieldSubview = {
  /** Agent-facing registry name. */
  readonly name: string;
  /** The clean-canonical measure family this sub-view projects. */
  readonly shape: Shape;
  /** The concern blurb spliced into the agent-facing description. */
  readonly concern: string;
};

/**
 * The three concern-split FIELD daily-rollup sub-views over `DailyRollupField_Agent`,
 * each de-prefixing one measure family to clean phase canonicals. Potential and
 * target are VOLUMES (no `*Rate` twin); allocated actuals carry volume + rate.
 */
const DAILY_ROLLUP_FIELD_SUBVIEWS: readonly DailyRollupFieldSubview[] = [
  {
    name: 'AllocatedOutputFieldDaily',
    shape: ALLOCATED_OUTPUT,
    concern: 'allocated oil/gas/water production and injection volumes and rates',
  },
  {
    name: 'PotentialOutputFieldDaily',
    shape: POTENTIAL_OUTPUT,
    concern: 'unconstrained potential oil/gas/water production and injection volumes',
  },
  {
    name: 'TargetOutputFieldDaily',
    shape: TARGET_OUTPUT_DAILY,
    concern: 'planning-target oil/gas/water production and injection volumes',
  },
];

/**
 * Build the daily-rollup surfaces: five wide grain views over the shared
 * `DAILY_ROLLUP` measures, plus the three concern-split FIELD sub-views. Pure
 * reshape views — no `deps`; each keeps identity + the grain relation. A hook
 * (not a plain function) because a reshape now SELF-MINTS its per-row ts-ref
 * cells (so derived columns keep their unit), which needs the CDF client +
 * session timeseries minter from `useCatalogEnrichContext`.
 */
export const useDailyRollupViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return [
    ...DAILY_ROLLUP_SPECS.map((spec: DailyRollupSpec) =>
      createReshapedView({
        name: spec.name,
        description: `Daily rollup of allocated oil/gas/water production and injection volumes and rates, plus unconstrained potential and planning target volumes (${spec.grain} grain). Filter by ${spec.grainRef} (or join the ${spec.grain.toLowerCase()} entity) to scope to a ${spec.grain.toLowerCase()}.`,
        source: spec.source,
        keep: [...IDENTITY, ...spec.relation],
        groups: [{ shape: DAILY_ROLLUP }],
        client,
        mintSeries,
      }),
    ),
    ...DAILY_ROLLUP_FIELD_SUBVIEWS.map((sub: DailyRollupFieldSubview) =>
      createReshapedView({
        name: sub.name,
        description: `Daily ${sub.concern} (FIELD grain), de-prefixed to clean per-phase column names (oil, gas, water, gasInjection, waterInjection). Filter by fieldEntity (or join the field entity) to scope to a field.`,
        source: DailyRollupFieldAgentView,
        keep: [...IDENTITY, ...FIELD_RELATION],
        groups: [{ shape: sub.shape }],
        client,
        mintSeries,
      }),
    ),
  ];
};
